import env from "../config";
import { UTMParams } from "../types";

// Build the public short URL for a tracker.
// `code` is the link's custom slug when set, otherwise its _id — both resolve
// at the clean /r/<code> route. If `customDomain` is provided (a verified
// default for the company) it's used as the host, else the backend's base URL.
// GHL merge field for the contact id — left literal (not URL-encoded) so GHL
// substitutes it at send time.
export const CONTACT_ID_MERGE = "{{contact.id}}";

export const buildRedirectUrl = (
  code: string,
  customDomain?: string | null,
  appendContactId?: boolean,
): string => {
  const path = `/r/${code}`;
  const base = customDomain
    ? `https://${customDomain.replace(/^https?:\/\//, "")}${path}`
    : `${env.REDIRECT_BASE}${path}`;
  return appendContactId ? `${base}?contact_id=${CONTACT_ID_MERGE}` : base;
};

// Public URL for a hosted landing page: <host>/p/<slug>.
export const buildPageUrl = (slug: string): string =>
  `${env.REDIRECT_BASE}/p/${slug}`;

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export const buildUtmUrl = (baseUrl: string, utm: UTMParams): string => {
  if (!baseUrl) return baseUrl;
  try {
    const url = new URL(baseUrl);
    if (utm.source) url.searchParams.set("utm_source", utm.source);
    if (utm.medium) url.searchParams.set("utm_medium", utm.medium);
    if (utm.campaign) url.searchParams.set("utm_campaign", utm.campaign);
    if (utm.term) url.searchParams.set("utm_term", utm.term);
    if (utm.content) url.searchParams.set("utm_content", utm.content);
    return url.toString();
  } catch {
    return baseUrl;
  }
};

export const parseUtmFromUrl = (
  url: string,
): { base: string; utm: UTMParams } => {
  try {
    const u = new URL(url);
    const utm: UTMParams = {
      source: u.searchParams.get("utm_source") || undefined,
      medium: u.searchParams.get("utm_medium") || undefined,
      campaign: u.searchParams.get("utm_campaign") || undefined,
      term: u.searchParams.get("utm_term") || undefined,
      content: u.searchParams.get("utm_content") || undefined,
    };
    UTM_KEYS.forEach((k) => u.searchParams.delete(k));
    return { base: u.toString().replace(/\?$/, ""), utm };
  } catch {
    return { base: url, utm: {} };
  }
};

// For <input type="datetime-local"> which uses local timezone, no Z.
export const toLocalInputValue = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const isExpired = (expiresAt?: string | null): boolean => {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
};

export const isExpiringSoon = (
  expiresAt?: string | null,
  withinHours = 24,
): boolean => {
  if (!expiresAt) return false;
  const ts = new Date(expiresAt).getTime();
  const now = Date.now();
  return ts > now && ts - now < withinHours * 60 * 60 * 1000;
};

export const formatNumber = (n: number | undefined | null): string => {
  if (n === undefined || n === null) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  return String(n);
};

export const formatDate = (iso?: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatDateTime = (iso?: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};
